import React, { useState, useEffect } from "react";
import AIOfferLetterGenerator from "./AIOfferLetterGenerator";
import UploadOfferLetter from "./UploadOfferLetter";
import OfferLetterPreviewPanel from "./OfferLetterPreviewPanel";
import OfferLetterViewPanel from "./OfferLetterViewPanel";
import CounterOfferModal from "./CounterOfferModal";
import SweetAlert from "react-bootstrap-sweetalert";
import { useCommonApi } from "src/_common/hooks/actions/common/appCommonApiHook";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { API_BASE_URL } from "src/_config";

interface FormData {
  candidateName: string;
  jobTitle: string;
  department: string;
  companyName: string;
  startDate: string;
  salary: string;
  salaryPeriod: string;
  currencyType: string;
  employmentType: string;
  benefits: string;
  includeDigitalSignature: boolean;
  workplace: string;
  workingHours: string;
  reportingTo: string;
  expiresAt: string;
}

interface OfferStatusProps {
  selectedCandidate: {
    id: number;
    name: string;
    position: string;
    bestMatch: boolean;
    offerStatus: {
      sent: { date: string; completed: boolean };
      accepted: { date: string; waiting: boolean; completed: boolean };
      declined: { date: string; notApplicable: boolean; completed: boolean; waiting?: boolean };
      status?: number;
    };
    fullCandidateData: any;
  };
  fetchFinalDecisionCandidates?: () => void;
}

export default function OfferStatus({ selectedCandidate, fetchFinalDecisionCandidates }: OfferStatusProps) {
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [showPreviewPanel, setShowPreviewPanel] = useState(false);
  const [showViewPanel, setShowViewPanel] = useState(false);
  const [showCounterOfferModal, setShowCounterOfferModal] = useState(false);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showDeclineInput, setShowDeclineInput] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [isRevisingOffer, setIsRevisingOffer] = useState(false);
  const [previewFormData, setPreviewFormData] = useState<FormData | null>(null);
  const [previewCurrencyType, setPreviewCurrencyType] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [offerLetterContent, setOfferLetterContent] = useState<string>("");
  const { t } = useTranslation("job-details");
  const toast = useToaster();

  const { callGenerateOfferLetter, callSendOfferLetter, callSendOfferLetterFile, callUpdateCounterOfferStatus } =
    useCommonApi();

  // Close all forms when the selected candidate changes
  useEffect(() => {
    setShowOfferForm(false);
    setShowUploadForm(false);
    setShowPreviewPanel(false);
    setShowViewPanel(false);
    setPreviewFormData(null);
    setIsRevisingOffer(false);
  }, [selectedCandidate.id]);

  const handleGenerateOfferClick = () => {
    const newShowState = !showOfferForm;
    setShowOfferForm(newShowState);
    setShowUploadForm(false); // Close upload form if open
    // If closing the form, reset revision mode and pre-filled data
    if (!newShowState) {
      setIsRevisingOffer(false);
      setPreviewFormData(null);
      setPreviewCurrencyType("");
    }
  };

  const handleUploadOfferClick = () => {
    const newShowState = !showUploadForm;
    setShowUploadForm(newShowState);
    setShowOfferForm(false); // Close generator form if open
    // If closing the form, reset revision mode
    if (!newShowState) {
      setIsRevisingOffer(false);
    }
  };

  const handleCancelOffer = () => {
    setShowOfferForm(false);
    setIsRevisingOffer(false);
    setPreviewFormData(null);
    setPreviewCurrencyType("");
  };

  const handleViewOfferClick = () => {
    console.log("Viewing offer letter for candidate:", selectedCandidate?.fullCandidateData);
    const offerLog = selectedCandidate?.fullCandidateData?.offer_letter_logs?.[0];

    if (offerLog?.offer_letter_content || offerLog?.offer_letter_content_signed) {
      // Always show offer_letter_content (not signed version)
      const contentToShow = offerLog.offer_letter_content;

      setOfferLetterContent(contentToShow);
      setShowViewPanel(true);
    } else if (offerLog?.temp_file_url) {
      // window.open(
      //   API_BASE_URL.replace("api/v1/", "") +
      //     selectedCandidate.fullCandidateData?.offer_letter_logs?.[0].offer_letter_file,
      //   "_blank"
      // );
      window.open(offerLog.temp_file_url, "_blank");
    }
  };

  const handleGenerateOffer = async (formData: FormData, currencyType: string) => {
    setIsGenerating(true);

    const requestData = {
      type: 2,
      screening_id: selectedCandidate.id.toString(),
      candidate_name: formData.candidateName,
      job_title: formData.jobTitle,
      department: formData.department,
      company_name: formData.companyName,
      start_date: formData.startDate,
      salary: formData.salary,
      currency_type: currencyType,
      employment_type: formData.employmentType,
      benefits: formData.benefits,
      digital_signature: formData.includeDigitalSignature,
    };

    callSendOfferLetter(
      requestData,
      (message: string, response: any) => {
        setIsGenerating(false);
        setShowOfferForm(false); // Close the form
        fetchFinalDecisionCandidates?.();
      },
      (error: string) => {
        setIsGenerating(false);
        toast.error(error);
      },
    );
  };

  const handlePreviewAndSend = async (formData: FormData, currencyType: string) => {
    // Store form data and currency type, clear previous content, then open preview panel
    setPreviewFormData({ ...formData, currencyType });
    setPreviewCurrencyType(currencyType);
    setOfferLetterContent(""); // Clear previous content to regenerate fresh HTML
    setShowPreviewPanel(true);
  };

  const handleSendOfferFromPreview = async () => {
    if (!previewFormData) return;

    setIsGenerating(true);

    const requestData = {
      type: 1,
      screening_id: selectedCandidate.id.toString(),
      offer_letter_content: offerLetterContent || "",
      digital_signature: previewFormData.includeDigitalSignature,
      currency_type: previewCurrencyType,
      candidate_name: previewFormData.candidateName,
      job_title: previewFormData.jobTitle,
      department: previewFormData.department,
      company_name: previewFormData.companyName,
      start_date: previewFormData.startDate,
      salary: previewFormData.salary,
      salary_period: previewFormData.salaryPeriod,
      employment_type: previewFormData.employmentType,
      benefits: previewFormData.benefits || "",
      workplace: previewFormData.workplace || "",
      working_hours_per_week: previewFormData.workingHours || "",
      reporting_to: previewFormData.reportingTo || "",
      expires_at: previewFormData.expiresAt || "",
    };

    callSendOfferLetter(
      requestData,
      (message: string, response: any) => {
        setIsGenerating(false);
        setShowPreviewPanel(false);
        setPreviewFormData(null);
        setPreviewCurrencyType("");
        setShowOfferForm(false); // Close the form
        setIsRevisingOffer(false); // Reset revision mode
        fetchFinalDecisionCandidates?.();
        toast.success(t("jobDetails.finalDecision.offerStatus.sendSuccess"));
      },
      (error: string) => {
        setIsGenerating(false);
      },
    );
  };

  const handleFileUpload = async (file: File) => {
    setIsUploading(true);

    const formData = new FormData();
    formData.append("screening_id", selectedCandidate.id.toString());
    formData.append("offer_letter_file", file);

    try {
      callSendOfferLetterFile(
        formData,
        (message: string, response: any) => {
          toast.success(t("jobDetails.finalDecision.offerStatus.uploadSuccess"));
          console.log("Uploaded offer letter:", response);
          setIsUploading(false);
        },
        (error: string) => {
          console.error("Error uploading offer letter:", error);
          toast.error(t("jobDetails.finalDecision.offerStatus.uploadError"));
          setIsUploading(false);
        },
      );
    } catch (error) {
      console.error("Error uploading offer letter:", error);
      toast.error(t("jobDetails.finalDecision.offerStatus.uploadError"));
      setIsUploading(false);
    }
  };

  const handleUploadCancel = () => {
    setShowUploadForm(false);
  };

  const handleSendUploadedFile = async (
    file: File,
    startDate: string,
    expiresAt: string,
    salary: string,
    currencyType: string,
  ) => {
    setIsUploading(true);

    const formData = new FormData();
    formData.append("screening_id", selectedCandidate.id.toString());
    formData.append("offer_letter_file", file);
    formData.append("start_date", startDate);
    formData.append("expires_at", expiresAt);
    if (salary) {
      formData.append("salary", salary);
    }
    if (currencyType) {
      formData.append("currency_type", currencyType);
    }

    callSendOfferLetterFile(
      formData,
      (message: string, response: any) => {
        toast.success(t("jobDetails.finalDecision.offerStatus.sendSuccess"));
        console.log("Sent uploaded offer letter:", response);
        setShowUploadForm(false);
        setIsUploading(false);
        setIsRevisingOffer(false); // Reset revision mode
        fetchFinalDecisionCandidates?.();
      },
      (error: string) => {
        console.error("Error sending offer letter:", error);
        toast.error(t("jobDetails.finalDecision.offerStatus.sendError"));
        setIsUploading(false);
      },
    );
  };

  useEffect(() => {
    console.log("Selected Candidate Offer Status:", selectedCandidate);
    const offerLog = selectedCandidate?.fullCandidateData?.offer_letter_logs?.[0];
    console.log("Counter Offer Status:", offerLog?.counter_offer_status);
    console.log("Has Counter Offer (pending):", hasCounterOffer());
    console.log("Has Approved Counter Offer:", hasApprovedCounterOffer());
  }, [selectedCandidate]);

  // Get counter offer data
  const getCounterOfferData = () => {
    const offerLog = selectedCandidate?.fullCandidateData?.offer_letter_logs?.[0];
    if (!offerLog) return null;

    return {
      originalOffer: {
        position: offerLog.job_title || selectedCandidate.position,
        salary: offerLog.salary || "",
        salaryPeriod: offerLog.salary_period || "monthly",
        currencyType: offerLog.currency_type || "INR",
        contractType: offerLog.employment_type || "Full-time",
        startDate: offerLog.start_date || "",
      },
      counterOffer: {
        requestedSalary: offerLog.counter_offer_requested_salary || "",
        preferredStartDate: offerLog.counter_offer_preferred_start_date || "",
        notes: offerLog.counter_offer_notes || "",
      },
    };
  };

  const hasCounterOffer = () => {
    const offerLog = selectedCandidate?.fullCandidateData?.offer_letter_logs?.[0];
    return (
      offerLog &&
      offerLog.counter_offer_status === 1 &&
      (offerLog.counter_offer_requested_salary ||
        offerLog.counter_offer_preferred_start_date ||
        offerLog.counter_offer_notes)
    );
  };

  const hasApprovedCounterOffer = () => {
    const offerLog = selectedCandidate?.fullCandidateData?.offer_letter_logs?.[0];
    return (
      offerLog &&
      offerLog.counter_offer_status === 2 &&
      (offerLog.counter_offer_requested_salary ||
        offerLog.counter_offer_preferred_start_date ||
        offerLog.counter_offer_notes)
    );
  };

  const hasRejectedCounterOffer = () => {
    const offerLog = selectedCandidate?.fullCandidateData?.offer_letter_logs?.[0];
    return (
      offerLog &&
      offerLog.counter_offer_status === 3 &&
      (offerLog.counter_offer_requested_salary ||
        offerLog.counter_offer_preferred_start_date ||
        offerLog.counter_offer_notes)
    );
  };

  // Determine offer letter type: 'generated' if employer first generated, 'uploaded' if first uploaded, null if none yet
  const getOfferLetterType = (): "generated" | "uploaded" | null => {
    const offerLog = selectedCandidate?.fullCandidateData?.offer_letter_logs?.[0];
    if (!offerLog) return null;

    if (offerLog.offer_letter_content) {
      return "generated";
    }
    if (offerLog.offer_letter_file || offerLog.temp_file_url) {
      return "uploaded";
    }
    return null;
  };

  const handleViewCounterOffer = () => {
    setShowCounterOfferModal(true);
  };

  const handleApproveCounterOffer = () => {
    setShowApproveConfirm(true);
  };

  const confirmApproveCounterOffer = () => {
    const offerLog = selectedCandidate?.fullCandidateData?.offer_letter_logs?.[0];
    if (!offerLog?.id) {
      toast.error("Offer letter log ID not found");
      setShowApproveConfirm(false);
      return;
    }

    callUpdateCounterOfferStatus(
      {
        offer_letter_log_id: offerLog.id,
        counter_offer_status: 2, // Approve
      },
      (message: string, response: any) => {
        toast.success("Counter offer approved successfully!");
        setShowApproveConfirm(false);
        setShowCounterOfferModal(false);
        fetchFinalDecisionCandidates?.();
      },
      (error: string) => {
        toast.error(error || "Failed to approve counter offer");
        setShowApproveConfirm(false);
      },
    );
  };

  const handleDeclineCounterOffer = () => {
    setShowDeclineInput(true);
  };

  const confirmDeclineCounterOffer = () => {
    if (!rejectionReason.trim()) {
      toast.error("Please provide a rejection reason");
      return;
    }

    const offerLog = selectedCandidate?.fullCandidateData?.offer_letter_logs?.[0];
    if (!offerLog?.id) {
      toast.error("Offer letter log ID not found");
      setShowDeclineInput(false);
      return;
    }

    callUpdateCounterOfferStatus(
      {
        offer_letter_log_id: offerLog.id,
        counter_offer_status: 3, // Decline
        rejection_reason: rejectionReason,
      },
      (message: string, response: any) => {
        toast.success("Counter offer declined successfully!");
        setShowDeclineInput(false);
        setShowCounterOfferModal(false);
        setRejectionReason("");
        fetchFinalDecisionCandidates?.();
      },
      (error: string) => {
        toast.error(error || "Failed to decline counter offer");
        setShowDeclineInput(false);
      },
    );
  };

  const handleSendRevisedOffer = () => {
    const offerLog = selectedCandidate?.fullCandidateData?.offer_letter_logs?.[0];
    if (!offerLog) return;

    const offerType = getOfferLetterType();

    // If the original offer was uploaded, open the upload form instead of the generator
    if (offerType === "uploaded") {
      setShowCounterOfferModal(false);
      setIsRevisingOffer(true);
      setShowUploadForm(true);
      setShowOfferForm(false);
      return;
    }

    // Pre-fill the form with counter offer values (for generated offers)
    const revisedFormData: FormData = {
      candidateName: offerLog.candidate_name || selectedCandidate.name,
      jobTitle: offerLog.job_title || selectedCandidate.position,
      department: offerLog.department || "",
      companyName: offerLog.company_name || "",
      startDate: offerLog.counter_offer_preferred_start_date || offerLog.start_date || "",
      salary: offerLog.counter_offer_requested_salary || offerLog.salary || "",
      salaryPeriod: offerLog.salary_period || "monthly",
      currencyType: offerLog.currency_type || "INR",
      employmentType: offerLog.employment_type || "Full-time",
      benefits: offerLog.benefits || "",
      includeDigitalSignature: offerLog.require_signature === 1,
      workplace: offerLog.workplace || "",
      workingHours: offerLog.working_hours_per_week || "",
      reportingTo: offerLog.reporting_to || "",
      expiresAt: "",
    };

    // Close modal and open the offer form with pre-filled data
    setShowCounterOfferModal(false);
    setIsRevisingOffer(true);
    setShowOfferForm(true);
    setPreviewFormData(revisedFormData);
    setPreviewCurrencyType(offerLog.currency_type || "INR");
  };

  return (
    <>
      {/* Offer Action Buttons - When Counter Offer is Approved */}
      {hasApprovedCounterOffer() && selectedCandidate?.offerStatus?.status !== 7 && (
        <div className="offer-actions">
          {getOfferLetterType() !== "uploaded" && (
            <button className="btn-get-hire" onClick={handleGenerateOfferClick}>
              <i className="fa fa-magic"></i>
              {t("jobDetails.finalDecision.offerStatus.generateOffer")}
            </button>
          )}
          {getOfferLetterType() !== "generated" && (
            <button
              className="btn-reject"
              style={{ backgroundColor: "#fff", color: "#ef4444", border: "1px solid #ef4444" }}
              onClick={handleUploadOfferClick}
            >
              <i className="fa fa-upload"></i>
              {t("jobDetails.finalDecision.offerStatus.uploadOffer")}
            </button>
          )}
          <button className="btn-get-hire" onClick={handleViewOfferClick}>
            {t("jobDetails.finalDecision.offerStatus.viewOffer")}
          </button>
          <button className="btn-counter-accept" onClick={handleViewCounterOffer}>
            <i className="fa fa-check-circle"></i>
            View Accepted Counter Offer
          </button>
        </div>
      )}

      {/* Offer Action Buttons - When Counter Offer is Rejected */}
      {hasRejectedCounterOffer() && selectedCandidate?.offerStatus?.status !== 7 && (
        <div className="offer-actions">
          {getOfferLetterType() !== "uploaded" && (
            <button className="btn-get-hire" onClick={handleGenerateOfferClick}>
              <i className="fa fa-magic"></i>
              {t("jobDetails.finalDecision.offerStatus.generateOffer")}
            </button>
          )}
          {getOfferLetterType() !== "generated" && (
            <button
              className="btn-reject"
              style={{ backgroundColor: "#fff", color: "#ef4444", border: "1px solid #ef4444" }}
              onClick={handleUploadOfferClick}
            >
              <i className="fa fa-upload"></i>
              {t("jobDetails.finalDecision.offerStatus.uploadOffer")}
            </button>
          )}
          <button className="btn-get-hire" onClick={handleViewOfferClick}>
            {t("jobDetails.finalDecision.offerStatus.viewOffer")}
          </button>
          <button className="btn-reject" onClick={handleViewCounterOffer}>
            <i className="fa fa-times-circle"></i>
            View Rejected Counter Offer
          </button>
        </div>
      )}

      {/* Offer Action Buttons - Normal Flow (no approved/rejected counter offer) */}
      {!hasApprovedCounterOffer() &&
        !hasRejectedCounterOffer() &&
        selectedCandidate?.offerStatus?.status !== 7 &&
        (selectedCandidate?.offerStatus?.status !== 6 || hasCounterOffer()) && (
          <div className="offer-actions">
            {!hasCounterOffer() && getOfferLetterType() !== "uploaded" && (
              <button className="btn-get-hire" onClick={handleGenerateOfferClick}>
                <i className="fa fa-magic"></i>
                {t("jobDetails.finalDecision.offerStatus.generateOffer")}
              </button>
            )}
            {!hasCounterOffer() && getOfferLetterType() !== "generated" && (
              <button
                className="btn-reject"
                style={{ backgroundColor: "#fff", color: "#ef4444", border: "1px solid #ef4444" }}
                onClick={handleUploadOfferClick}
              >
                <i className="fa fa-upload"></i>
                {t("jobDetails.finalDecision.offerStatus.uploadOffer")}
              </button>
            )}
            {(selectedCandidate?.offerStatus?.status == 8 || hasCounterOffer()) && (
              <button className="btn-get-hire" onClick={handleViewOfferClick}>
                {t("jobDetails.finalDecision.offerStatus.viewOffer")}
              </button>
            )}
            {hasCounterOffer() && (
              <button className="btn-counter-accept" onClick={handleViewCounterOffer}>
                <i className="fa fa-handshake-o"></i>
                View Counter Offer
              </button>
            )}
          </div>
        )}

      {/* Show only View Offer button when offer is accepted (status == 7) */}
      {selectedCandidate?.offerStatus?.status === 7 && (
        <div className="offer-actions">
          <button className="btn-get-hire" onClick={handleViewOfferClick}>
            <i className="fa fa-check-circle"></i>
            View Accepted Offer
          </button>
        </div>
      )}

      {/* Show View Offer button when in Awaited state (status == 6) and NO counter offer and NO approved/rejected counter offer */}
      {selectedCandidate?.offerStatus?.status === 6 &&
        !hasCounterOffer() &&
        !hasApprovedCounterOffer() &&
        !hasRejectedCounterOffer() && (
          <div className="offer-actions">
            <button className="btn-get-hire" onClick={handleViewOfferClick}>
              {t("jobDetails.finalDecision.offerStatus.viewOffer")}
            </button>
          </div>
        )}

      {/* AI Offer Letter Generator Form */}
      {showOfferForm && (
        <AIOfferLetterGenerator
          candidateData={{
            name: selectedCandidate.name,
            position: selectedCandidate.position,
          }}
          // onGenerate={handleGenerateOffer}
          onPreviewAndSend={handlePreviewAndSend}
          isGenerating={isGenerating}
          initialData={isRevisingOffer ? previewFormData || undefined : undefined}
          initialCurrencyType={isRevisingOffer ? previewCurrencyType || undefined : undefined}
          onCancel={handleCancelOffer}
          showCancelButton={hasCounterOffer()}
        />
      )}

      {/* Upload Offer Letter Form */}
      {showUploadForm && selectedCandidate && (
        <UploadOfferLetter
          candidateData={{
            name: selectedCandidate.name,
            position: selectedCandidate.position,
          }}
          onUpload={handleFileUpload}
          onCancel={handleUploadCancel}
          onSendToCandidate={handleSendUploadedFile}
          isUploading={isUploading}
        />
      )}

      {/* Offer Letter Preview Panel */}
      {showPreviewPanel && previewFormData && selectedCandidate && (
        <OfferLetterPreviewPanel
          isOpen={showPreviewPanel}
          onClose={() => {
            setShowPreviewPanel(false);
            setOfferLetterContent(""); // Clear content on close
          }}
          formData={previewFormData}
          candidateId={selectedCandidate.id}
          onSendToCandidate={handleSendOfferFromPreview}
          offerLetterContent={offerLetterContent}
          setOfferLetterContent={setOfferLetterContent}
          candidateData={selectedCandidate}
        />
      )}

      {/* Offer Letter View Panel */}
      {showViewPanel && selectedCandidate && (
        <OfferLetterViewPanel
          isOpen={showViewPanel}
          onClose={() => setShowViewPanel(false)}
          offerLetterContent={offerLetterContent}
          jobTitle={selectedCandidate.position}
          status={selectedCandidate?.offerStatus?.status}
          candidateSignatureMode={
            selectedCandidate?.fullCandidateData?.offer_letter_logs?.[0]?.candidate_signature_mode
          }
          candidateSignatureUrl={selectedCandidate?.fullCandidateData?.offer_letter_logs?.[0]?.candidate_signature_url}
          candidateSignatureName={
            selectedCandidate?.fullCandidateData?.offer_letter_logs?.[0]?.candidate_signature_name
          }
          declineReason={selectedCandidate?.fullCandidateData?.offer_letter_logs?.[0]?.decline_reason}
        />
      )}

      <div className="offer-status">
        <h3>{t("jobDetails.finalDecision.offerStatus.title")}</h3>
        <div className="status-timeline">
          {/* Status 1: Offer Sent */}
          <div className={`status-step ${selectedCandidate.offerStatus.sent.completed ? "completed" : ""}`}>
            <div className="status-icon">
              {selectedCandidate.offerStatus.sent.completed ? <i className="fa fa-check"></i> : <span>1</span>}
            </div>
            <div className="status-info">
              <h4>{t("jobDetails.finalDecision.offerStatus.timeline.offerSent")}</h4>
              <p>
                {selectedCandidate.offerStatus.sent.date || t("jobDetails.finalDecision.offerStatus.timeline.notSent")}
              </p>
            </div>
          </div>

          {/* <div className={`status-step ${selectedCandidate.offerStatus.accepted.completed ? "completed" : "inactive"}`}>
            <div className="status-icon">
              {selectedCandidate.offerStatus.accepted.completed ? <i className="fa fa-check"></i> : <span>2</span>}
            </div>
            <div className="status-info">
              <h4>{t("jobDetails.finalDecision.offerStatus.timeline.accepted")}</h4>
              <p>
                {selectedCandidate.offerStatus.accepted.completed
                  ? t("jobDetails.finalDecision.offerStatus.timeline.offerAccepted")
                  : selectedCandidate.offerStatus.accepted.waiting
                  ? t("jobDetails.finalDecision.offerStatus.timeline.waitingResponse")
                  : t("jobDetails.finalDecision.offerStatus.timeline.notApplicable")}
              </p>
            </div>
          </div>

          <div className={`status-step ${selectedCandidate.offerStatus.declined.completed ? "completed" : "inactive"}`}>
            <div className="status-icon">
              {selectedCandidate.offerStatus.declined.completed ? <i className="fa fa-check"></i> : <span>3</span>}
            </div>
            <div className="status-info">
              <h4>{t("jobDetails.finalDecision.offerStatus.timeline.declined")}</h4>
              <p>
                {selectedCandidate.offerStatus.declined.completed
                  ? t("jobDetails.finalDecision.offerStatus.timeline.offerDeclined")
                  : !selectedCandidate.offerStatus.declined.notApplicable &&
                    selectedCandidate.offerStatus.sent.completed
                  ? t("jobDetails.finalDecision.offerStatus.timeline.waitingResponse")
                  : t("jobDetails.finalDecision.offerStatus.timeline.notApplicable")}
              </p>
            </div>
          </div> */}

          {/* Status 2: Offer Response Status */}
          {(() => {
            const { sent, accepted, declined } = selectedCandidate.offerStatus;
            const offerLog = selectedCandidate?.fullCandidateData?.offer_letter_logs?.[0];
            const counterOfferStatus = offerLog?.counter_offer_status; // 1=pending, 2=approved, 3=declined
            const hasCounterData =
              offerLog &&
              (offerLog.counter_offer_requested_salary ||
                offerLog.counter_offer_preferred_start_date ||
                offerLog.counter_offer_notes);

            // Helper function to format date (DD/MM/YYYY format)
            const formatDate = (dateString: string) => {
              if (!dateString) return "";
              const date = new Date(dateString);
              const day = String(date.getDate()).padStart(2, "0");
              const month = String(date.getMonth() + 1).padStart(2, "0");
              const year = date.getFullYear();
              return `${day}/${month}/${year}`;
            };

            // Determine the status and styling
            let statusClass = "inactive";
            let statusTitle = "Candidate Response";
            let statusText = "Not Applicable";
            let statusDateClass = "";

            if (declined.completed) {
              // Offer was declined
              statusClass = "completed declined";
              statusTitle = "Offer Declined";
              statusText = declined.date || "Candidate declined the offer";
              statusDateClass = "offer-declined-date";
            } else if (accepted.completed) {
              // Offer was accepted
              statusClass = "completed accepted";
              statusTitle = "Offer Accepted";
              statusText = accepted.date || "Candidate accepted the offer";
              statusDateClass = "offer-accepted-date";
            } else if (hasCounterData && counterOfferStatus === 2) {
              // Counter offer approved by employer
              statusClass = "completed accepted";
              statusTitle = "Counter Offer Approved";
              statusText = offerLog.counter_offer_responded_at
                ? formatDate(offerLog.counter_offer_responded_at)
                : "You approved the candidate's counter offer";
              statusDateClass = "offer-accepted-date";
            } else if (hasCounterData && counterOfferStatus === 3) {
              // Counter offer declined by employer
              statusClass = "completed declined";
              statusTitle = "Counter Offer Rejected";
              statusText = offerLog.counter_offer_responded_at
                ? formatDate(offerLog.counter_offer_responded_at)
                : "You rejected the candidate's counter offer";
              statusDateClass = "offer-declined-date";
            } else if (hasCounterData && counterOfferStatus === 1) {
              // Counter offer pending (status 1)
              statusClass = "active";
              statusTitle = "Counter Offer Received";
              statusText = offerLog.counter_offer_requested_at
                ? formatDate(offerLog.counter_offer_requested_at)
                : "Review and respond to counter offer";
            } else if (sent.completed) {
              // Offer sent, waiting for response (no counter offer)
              statusClass = "active";
              statusTitle = "Awaited";
              statusText = "Awaiting candidate's response";
            }

            return (
              <div className={`status-step ${statusClass}`}>
                <div className="status-icon">
                  {declined.completed || counterOfferStatus === 3 ? (
                    <i className="fa fa-times"></i>
                  ) : accepted.completed || counterOfferStatus === 2 ? (
                    <i className="fa fa-check"></i>
                  ) : (
                    <span>2</span>
                  )}
                </div>
                <div className="status-info">
                  <h4>{statusTitle}</h4>
                  <p className={statusDateClass}>{statusText}</p>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Counter Offer Modal */}
      {showCounterOfferModal && getCounterOfferData() && !showApproveConfirm && !showDeclineInput && (
        <CounterOfferModal
          isOpen={showCounterOfferModal}
          onClose={() => setShowCounterOfferModal(false)}
          counterOfferData={getCounterOfferData()!}
          onApprove={handleApproveCounterOffer}
          onDecline={handleDeclineCounterOffer}
          onSendRevised={handleSendRevisedOffer}
          isReadOnly={
            selectedCandidate?.fullCandidateData?.offer_letter_logs?.[0]?.counter_offer_status === 2 ||
            selectedCandidate?.fullCandidateData?.offer_letter_logs?.[0]?.counter_offer_status === 3
          }
          isRejected={selectedCandidate?.fullCandidateData?.offer_letter_logs?.[0]?.counter_offer_status === 3}
        />
      )}

      {/* Approve Counter Offer Confirmation */}
      {showApproveConfirm && (
        <SweetAlert
          warning
          showCancel
          confirmBtnText="Yes, Approve"
          confirmBtnBsStyle="preview"
          cancelBtnText="Cancel"
          cancelBtnBsStyle="cancel"
          title="Approve Counter Offer?"
          onConfirm={confirmApproveCounterOffer}
          onCancel={() => setShowApproveConfirm(false)}
        >
          Are you sure you want to approve this counter offer? This will accept the candidate's proposed terms.
        </SweetAlert>
      )}

      {/* Decline Counter Offer with Rejection Reason */}
      {showDeclineInput && (
        <SweetAlert
          warning
          showCancel
          confirmBtnText="Submit Decline"
          confirmBtnBsStyle="preview"
          cancelBtnText="Cancel"
          cancelBtnBsStyle="cancel"
          title="Decline Counter Offer"
          onConfirm={confirmDeclineCounterOffer}
          onCancel={() => {
            setShowDeclineInput(false);
            setRejectionReason("");
          }}
          customClass="decline-counter-offer-alert"
        >
          <div style={{ marginBottom: "15px" }}>Please provide a reason for declining the counter offer:</div>
          <textarea
            className="form-control"
            rows={4}
            placeholder="Enter rejection reason..."
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #ddd",
              borderRadius: "4px",
              fontSize: "14px",
            }}
          />
        </SweetAlert>
      )}
    </>
  );
}
